import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge, Card, Empty, ErrorBox, Skeleton } from "@/components/ui";

type ClinicSession = {
  id: string;
  clinic_date: string;
  clinic_name: string;
  source: string;
  status: string;
  counts: Record<string, number>;
  rows: Array<{ id: string; patient_id: string; full_name: string; mrn: string; clinic_status: string; prep_status: string }>;
};

export default function ClinicPrep() {
  const qc = useQueryClient();
  const [clinicDate, setClinicDate] = useState(new Date().toISOString().slice(0, 10));
  const [clinicName, setClinicName] = useState("Radiation Oncology clinic");
  const [rawText, setRawText] = useState("");
  const [uploadResult, setUploadResult] = useState<any | null>(null);
  const sessions = useQuery({
    queryKey: ["clinic-prep-sessions"],
    queryFn: () => api.get<ClinicSession[]>("/clinic-prep/sessions"),
  });
  const create = useMutation({
    mutationFn: () => api.post<ClinicSession>("/clinic-prep/sessions", {
      clinic_date: clinicDate,
      clinic_name: clinicName,
      raw_text: rawText,
      source: "pasted_text",
    }),
    onSuccess: () => {
      setRawText("");
      qc.invalidateQueries({ queryKey: ["clinic-prep-sessions"] });
      qc.invalidateQueries({ queryKey: ["patients"] });
    },
  });
  const reconcile = useMutation({
    mutationFn: (id: string) => api.post<ClinicSession>(`/clinic-prep/sessions/${id}/reconcile`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clinic-prep-sessions"] });
    },
  });
  const uploadImage = useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append("clinic_date", clinicDate);
      fd.append("clinic_name", clinicName);
      fd.append("file", file);
      return api.upload<any>("/clinic-prep/image-upload", fd);
    },
    onSuccess: (data) => {
      setUploadResult(data);
      if (data.extracted_text) setRawText(data.extracted_text);
    },
  });
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-50">Clinic prep</h1>
        <div className="text-sm text-slate-400">Paste a clinic list to create scheduled patient shells without clinical suggestions.</div>
      </div>
      <Card>
        <div className="grid md:grid-cols-[180px_1fr] gap-3">
          <div><label className="label">Clinic date</label><input className="input" type="date" value={clinicDate} onChange={(e) => setClinicDate(e.target.value)} /></div>
          <div><label className="label">Clinic name</label><input className="input" value={clinicName} onChange={(e) => setClinicName(e.target.value)} /></div>
        </div>
        <div className="mt-3"><label className="label">Clinic list</label><textarea className="input min-h-[180px] font-mono text-xs" value={rawText} onChange={(e) => setRawText(e.target.value)} placeholder={"Maryam Ebrahim Ali Najmi 1186692\nAnother Patient MRN 1234567"} /></div>
        <div className="mt-3 grid md:grid-cols-[260px_1fr] gap-3 items-start">
          <div>
            <label className="label">Upload clinic list image</label>
            <input type="file" accept="image/*,.pdf" className="block text-sm" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadImage.mutate(f); }} />
          </div>
          <div className="text-xs text-slate-400">
            {uploadImage.isPending ? "Uploading..." : uploadResult ? `${uploadResult.ocr_status}. Review text before creating the session.` : "If OCR is unavailable, the image is stored and RPIS asks you to paste or confirm the text."}
          </div>
        </div>
        {uploadImage.error ? <div className="mt-3"><ErrorBox error={uploadImage.error} /></div> : null}
        <div className="mt-3"><button className="btn-primary" disabled={!rawText.trim() || create.isPending} onClick={() => create.mutate()}>{create.isPending ? "Creating..." : "Create clinic session"}</button></div>
        {create.error ? <div className="mt-3"><ErrorBox error={create.error} /></div> : null}
      </Card>
      {sessions.error ? <ErrorBox error={sessions.error} /> : null}
      {sessions.isLoading ? <Card><Skeleton rows={6} /></Card> : !sessions.data?.length ? <Empty title="No clinic sessions yet." /> : (
        <div className="space-y-4">
          {sessions.data.map((session) => (
            <Card key={session.id} title={`${session.clinic_name} · ${session.clinic_date}`} actions={<button className="btn-ghost" disabled={reconcile.isPending} onClick={() => reconcile.mutate(session.id)}>Reconcile after clinic</button>}>
              <div className="flex flex-wrap gap-2 mb-3 text-xs">
                {Object.entries(session.counts).map(([k, v]) => <Badge key={k} tone="info">{k}: {v}</Badge>)}
              </div>
              <table className="table">
                <thead><tr><th>MRN</th><th>Patient</th><th>Clinic status</th><th>Prep</th></tr></thead>
                <tbody>{session.rows.map((row) => (
                  <tr key={row.id}>
                    <td className="font-mono text-xs text-slate-400">{row.mrn}</td>
                    <td><Link className="text-accent hover:underline" to={`/patients/${row.patient_id}`}>{row.full_name}</Link></td>
                    <td><Badge tone={row.clinic_status.includes("follow") ? "warn" : "info"}>{row.clinic_status}</Badge></td>
                    <td><Badge tone={row.prep_status === "data_missing" ? "warn" : "muted"}>{row.prep_status}</Badge></td>
                  </tr>
                ))}</tbody>
              </table>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
